import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import MangaCard from '@/pages/home/components/MangaCard';
import { getMangaById, getRecommendManga } from '@/mocks/manga';
import type { MangaItem } from '@/mocks/manga';

export default function MangaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const manga = id ? getMangaById(id) : undefined;
  const [showAllChapters, setShowAllChapters] = useState(false);

  if (!manga) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-16 text-center px-4">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-200">
            <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex items-center px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const recommendManga = getRecommendManga(manga.id);
  const displayedChapters = showAllChapters ? manga.chapters : manga.chapters.slice(0, 10);
  const tagColors = ['bg-primary-100 text-primary-700', 'bg-accent-100 text-accent-700', 'bg-secondary-100 text-secondary-700'];

  const categorySlug = manga.category === '韩漫' ? 'korean' : manga.category === '日漫' ? 'japanese' : 'chinese';

  const breadcrumbs = [
    { label: '首页', path: '/' },
    { label: manga.category, path: `/category/${categorySlug}` },
    { label: manga.title, path: `/manga/${manga.id}` },
  ];

  return (
    <>
      <Navbar />
      <ScrollToTop />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-3">
            <nav aria-label="面包屑导航" className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              {breadcrumbs.map((crumb, idx) => (
                <span key={crumb.path} className="flex items-center gap-2">
                  {idx > 0 && (
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line"></i>
                    </div>
                  )}
                  {idx === breadcrumbs.length - 1 ? (
                    <span className="text-foreground-700 font-medium">{crumb.label}</span>
                  ) : (
                    <Link to={crumb.path} className="hover:text-primary-500 transition-colors cursor-pointer">
                      {crumb.label}
                    </Link>
                  )}
                </span>
              ))}
            </nav>
          </div>
        </div>

        <article className="py-8 md:py-10 px-4 md:px-6 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="w-48 md:w-56 h-72 md:h-80 rounded-xl overflow-hidden bg-background-200">
                  <img
                    src={manga.coverImage}
                    alt={manga.title}
                    title={`${manga.title} - 樱花漫画 ${manga.category}漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                    {manga.category}
                  </span>
                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                    manga.status === '连载中'
                      ? 'bg-secondary-100 text-secondary-700'
                      : 'bg-foreground-200 text-foreground-700'
                  }`}>
                    {manga.status}
                  </span>
                  {manga.isHot && (
                    <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
                      热门
                    </span>
                  )}
                </div>

                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                  {manga.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-600 mb-5">
                  <span>
                    <i className="ri-user-line mr-1"></i>
                    {manga.author}
                  </span>
                  <span className="flex items-center">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-400 text-sm"></i>
                    </div>
                    <span className="ml-1 font-medium text-foreground-900">{manga.rating}</span>
                  </span>
                  <span>
                    <i className="ri-eye-line mr-1"></i>
                    {manga.views >= 10000 ? `${(manga.views / 10000).toFixed(0)}万` : manga.views} 阅读
                  </span>
                  <span>
                    <i className="ri-file-list-3-line mr-1"></i>
                    共 {manga.chapters.length} 话
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-5">
                  {manga.tags.map((tag, idx) => (
                    <span
                      key={tag.id}
                      className={`px-3 py-1 text-xs rounded-full whitespace-nowrap ${tagColors[idx % tagColors.length]}`}
                    >
                      {tag.name}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-xl p-5 mb-6">
                  <h2 className="font-heading text-sm font-semibold text-foreground-900 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {manga.longDescription}
                  </p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Link
                    to={`/manga/${manga.id}`}
                    className="px-8 py-3 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-all duration-200 text-sm cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-play-circle-line mr-2"></i>
                    开始阅读
                  </Link>
                  <button className="px-6 py-3 bg-background-100 text-foreground-700 font-medium rounded-full hover:bg-background-200 transition-all duration-200 text-sm cursor-pointer whitespace-nowrap border border-background-200/70">
                    <i className="ri-bookmark-line mr-2"></i>
                    加入收藏
                  </button>
                  <button className="px-6 py-3 bg-background-100 text-foreground-700 font-medium rounded-full hover:bg-background-200 transition-all duration-200 text-sm cursor-pointer whitespace-nowrap border border-background-200/70">
                    <i className="ri-notification-3-line mr-2"></i>
                    追更
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        <section className="py-8 md:py-10 px-4 md:px-6 bg-background-100">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-lg font-bold text-foreground-900">
                <i className="ri-file-list-3-line mr-2 text-primary-500"></i>
                章节列表
              </h2>
              <span className="text-xs text-foreground-500">共 {manga.chapters.length} 话 · {manga.status === '连载中' ? '每周更新' : '已完结'}</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
              {displayedChapters.map((chapter) => (
                <a
                  key={chapter.id}
                  href={`#chapter-${chapter.number}`}
                  className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 hover:bg-primary-50 transition-colors cursor-pointer group"
                >
                  <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors truncate">
                    {chapter.title}
                  </span>
                  <span className="text-xs text-foreground-400 whitespace-nowrap ml-3">
                    {chapter.updateDate}
                  </span>
                </a>
              ))}
            </div>

            {manga.chapters.length > 10 && (
              <div className="text-center mt-6">
                <button
                  onClick={() => setShowAllChapters(!showAllChapters)}
                  className="px-6 py-2.5 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  {showAllChapters ? '收起' : `查看全部 ${manga.chapters.length} 话`}
                  <span className="w-4 h-4 inline-flex items-center justify-center ml-1">
                    {showAllChapters ? <i className="ri-arrow-up-s-line"></i> : <i className="ri-arrow-down-s-line"></i>}
                  </span>
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="py-8 md:py-10 px-4 md:px-6 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <h2 className="font-heading text-lg font-bold text-foreground-900 mb-6">
              <i className="ri-heart-line mr-2 text-primary-500"></i>
              猜你喜欢
            </h2>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {recommendManga.map((item) => (
                <MangaCard key={item.id} manga={item} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Book',
            name: manga.title,
            author: { '@type': 'Person', name: manga.author },
            description: manga.description,
            genre: manga.tags.map((t) => t.name),
            inLanguage: 'zh-CN',
            url: `https://www.yinghuamanhua.com/manga/${manga.id}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '樱花漫画',
              url: 'https://www.yinghuamanhua.com',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.yinghuamanhua.com' },
                { '@type': 'ListItem', position: 2, name: manga.category, item: `https://www.yinghuamanhua.com/category/${categorySlug}` },
                { '@type': 'ListItem', position: 3, name: manga.title, item: `https://www.yinghuamanhua.com/manga/${manga.id}` },
              ],
            },
          }),
        }}
      />
    </>
  );
}